import { useCallback, useState, useEffect, useRef } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { callPost } from '@/api/fetcher';
import {
  SidebarWrapperContent,
  SidebarWrapperFooter,
} from '@/components/atoms/SidebarWrapper/SidebarWrapper';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  FormField,
  FormControl,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import useDatasources from '@/hooks/useDatasources';
import { cn } from '@/lib/utils';
import { openPublishModal } from '@/redux/collections/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { Datasource, DatasourceType } from '@/shared/types/collections';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { extractNames, generateUniqueName, noop } from '@/util/util';

import { EXTERNAL_SOURCE_FIELD, EXTERNAL_SOURCE_FOOTER, EXTERNAL_SOURCE_SECTION } from './layout';
import { useExternalSourceDatasourceId } from './useExternalSourceDatasourceId';

interface WorkvivoSpace {
  id: number;
  parent_space_id: number | null;
  name: string;
  description: string;
  visibility: string;
  is_external: boolean;
  is_corporate: boolean;
  is_mandatory: boolean;
  is_read_only: boolean;
  icon_url?: string;
  header_thumb_url?: string;
  header_url?: string;
  categories?: Array<{
    id: number;
    name: string;
    description: string | null;
    created_at: string;
    updated_at: string;
  }>;
  created_at: string;
  updated_at: string;
  permalink: string;
}

interface WorkvivoConfig {
  client_id: string;
  access_token: string;
  space_ids?: string[];
  pages?: boolean;
  articles?: boolean;
  goals?: boolean;
}

type WorkvivoFormProps = { handleCancel: (withValidation?: boolean) => void };

/**
 * Skeleton component for loading spaces
 */
const SpaceSkeleton = () => {
  return (
    <div className="space-y-2">
      <Skeleton className="h-4 w-20" />
      <Skeleton className="h-10 w-full" />
    </div>
  );
};

/**
 * WorkvivoForm component for configuring Workvivo integration
 * Allows users to input client ID and token for authentication and space selection
 */
export const WorkvivoForm = ({ handleCancel }: WorkvivoFormProps) => {
  // Redux selectors
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'collections', actions.WRITE)
  );
  const dispatch = useDispatch();

  // Initial mount ref to prevent multiple data fetches
  const initializedRef = useRef(false);

  // Ref to track if we're currently fetching to prevent multiple simultaneous calls
  const isFetchingRef = useRef(false);

  // Ref to track previous credentials to avoid unnecessary API calls
  const previousCredentialsRef = useRef<{ clientId: string; accessToken: string }>({
    clientId: '',
    accessToken: '',
  });

  // Hooks
  const { collectionId } = useParams();
  const form = useFormContext();
  const { t } = useTranslation();
  // URL first: redux only applies on the list page, where the URL has no datasource id.
  const datasourceId = useExternalSourceDatasourceId();
  const { datasource, getStatus, createDatasource, updateDatasource, datasources } = useDatasources(
    collectionId,
    datasourceId
  );

  const isWorkvivoEditing =
    !!datasourceId && getStatus === 'success' && datasource.type === DatasourceType.WORKVIVO_KB;

  // Type guard for Workvivo config
  const isWorkvivoConfig = (config: unknown): config is WorkvivoConfig => {
    return (
      config !== null &&
      typeof config === 'object' &&
      'client_id' in config &&
      'access_token' in config
    );
  };

  // Local state
  const [workvivoSpaces, setWorkvivoSpaces] = useState<WorkvivoSpace[]>([]);
  const [selectedSpaceIds, setSelectedSpaceIds] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);
  const [isSpacesLoading, setIsSpacesLoading] = useState(false);
  const [showSpacesSection, setShowSpacesSection] = useState(false);

  // Local variables
  const clientId = useWatch({ control: form.control, name: 'client_id' });
  const accessToken = useWatch({ control: form.control, name: 'access_token' });
  const pages = useWatch({ control: form.control, name: 'pages' });
  const articles = useWatch({ control: form.control, name: 'articles' });
  const goals = useWatch({ control: form.control, name: 'goals' });
  const spaceIds = useWatch({ control: form.control, name: 'space_ids' });

  const showSpaces = workvivoSpaces.length > 0 && clientId && accessToken;
  const showContentTypes = showSpaces;
  const showButtons = clientId && accessToken;

  // Validation: Check if at least one section is selected OR at least one space is selected
  const hasSelectedSections = pages || articles || goals;
  const hasSelectedSpaces = spaceIds && spaceIds.length > 0;
  const isSubmitDisabled = isValidating || (!hasSelectedSections && !hasSelectedSpaces);

  const showKeepPublished =
    isWorkvivoEditing && !datasource?.is_draft && datasource?.status === 'available';

  // Sync form values with local state - only run when form changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'space_ids') {
        const spaceIds = value.space_ids || [];
        setSelectedSpaceIds(spaceIds);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  /**
   * Handles space selection changes
   */
  const handleSpaceSelection = useCallback(
    (spaceId: string, checked: boolean) => {
      const newSelectedSpaces = checked
        ? [...selectedSpaceIds, spaceId]
        : selectedSpaceIds.filter((id) => id !== spaceId);

      setSelectedSpaceIds(newSelectedSpaces);
      form.setValue('space_ids', newSelectedSpaces, { shouldDirty: !isWorkvivoEditing });
    },
    [selectedSpaceIds, form, isWorkvivoEditing]
  );

  /**
   * Handles select all spaces
   */
  const handleSelectAllSpaces = useCallback(
    (checked: boolean) => {
      const newSelectedSpaces = checked ? workvivoSpaces.map((space) => space.id.toString()) : [];
      setSelectedSpaceIds(newSelectedSpaces);
      form.setValue('space_ids', newSelectedSpaces, { shouldDirty: !isWorkvivoEditing });
    },
    [workvivoSpaces, form, isWorkvivoEditing]
  );

  /**
   * Fetches Workvivo spaces - simple async function without useCallback
   */
  const fetchWorkvivoSpaces = async (client_id: string, access_token: string) => {
    if (!client_id || !access_token || isFetchingRef.current) {
      return;
    }

    // Check if credentials have actually changed
    if (
      previousCredentialsRef.current.clientId === client_id &&
      previousCredentialsRef.current.accessToken === access_token
    ) {
      return;
    }

    previousCredentialsRef.current = { clientId: client_id, accessToken: access_token };
    isFetchingRef.current = true;
    setIsValidating(true);
    setIsSpacesLoading(true);

    try {
      // Make API call to backend to validate credentials and fetch spaces
      const response = await callPost('/www/url-validation/workvivo/spaces', {
        client_id,
        access_token,
      });

      setWorkvivoSpaces(response.data || []);

      // Select all spaces by default
      const allSpaceIds = response.data?.map((space: WorkvivoSpace) => space.id.toString()) || [];
      setSelectedSpaceIds(allSpaceIds);
      form.setValue('space_ids', allSpaceIds, { shouldDirty: !isWorkvivoEditing });

      // Automatically show spaces section when spaces are loaded
      if (response.data && response.data.length > 0) {
        setShowSpacesSection(true);
      }

      // Clear any previous errors
      form.clearErrors('client_id');
      form.clearErrors('access_token');
    } catch (_error) {
      // Set error on the access_token field as it's the last required field
      form.setError('access_token', { message: t('integrations.workvivo.validation_error') });
      setWorkvivoSpaces([]);
      setSelectedSpaceIds([]);
      setShowSpacesSection(false);
    } finally {
      isFetchingRef.current = false;
      setIsValidating(false);
      setIsSpacesLoading(false);
    }
  };

  // Trigger validation when credentials change
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (clientId && accessToken) {
        fetchWorkvivoSpaces(clientId, accessToken);
      } else {
        // Clear spaces when credentials are incomplete
        setWorkvivoSpaces([]);
        setSelectedSpaceIds([]);
        setShowSpacesSection(false);
        form.setValue('space_ids', [], { shouldDirty: false });
        // Reset previous credentials when clearing
        previousCredentialsRef.current = { clientId: '', accessToken: '' };
      }
    }, 500); // Debounce API calls

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, accessToken]); // Only depend on the actual credential values - fetchWorkvivoSpaces intentionally excluded to prevent infinite loops

  /**
   * Handles form submission for Workvivo datasource
   */
  const handleSubmit = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Prevent the form from being submitted
      e.preventDefault();

      const { client_id, access_token, space_ids, pages, articles, goals, keep_published } =
        form.getValues();

      // Create a base name for the Workvivo datasource
      const baseName = `${t('integrations.workvivo.name')} ${client_id}`;

      // Use the generateUniqueName utility to create a unique name
      const datasourceName = isWorkvivoEditing
        ? datasource.name || baseName
        : generateUniqueName(baseName, extractNames(datasources || []));

      const newDatasource: Partial<Datasource> = {
        type: DatasourceType.WORKVIVO_KB,
        name: datasourceName,
        config: {
          client_id,
          access_token,
          space_ids: space_ids || selectedSpaceIds,
          pages: pages ?? true,
          articles: articles ?? true,
          goals: goals ?? true,
        },
      };

      if (isWorkvivoEditing) {
        updateDatasource(
          {
            datasource_id: datasource.datasource_id,
            collection_id: datasource.collection_id,
            ...newDatasource,
            is_draft: !keep_published,
          },
          {
            onSuccess: () => {
              toast.success(t('collections.toast_messages.external_source_updated'));
              handleCancel();
            },
            onError: () => {
              toast.error(t('collections.toast_messages.external_source_not_connected'));
            },
          }
        );
      } else {
        createDatasource(newDatasource, {
          onSuccess: () => {
            toast(t('collections.toast_messages.external_source_uploaded'), {
              description: t('collections.toast_messages.external_source_draft'),
              action: {
                label: t('collections.publish'),
                onClick: () => {
                  dispatch(openPublishModal());
                },
              },
            });
            handleCancel();
          },
          onError: () => {
            toast(t('collections.toast_messages.external_source_not_connected'), {
              action: { onClick: noop, label: t('collections.publish') },
            });

            toast.custom((id) => (
              <div
                className={cn(
                  'flex w-full max-w-md items-center justify-between rounded-lg border border-border bg-background p-6 shadow-md animate-in fade-in slide-in-from-top-full'
                )}
                role="alert"
              >
                <div className="flex-1">
                  <h2 className="text-sm leading-5 font-semibold">
                    {t('collections.toast_messages.external_source_not_connected')}
                  </h2>
                </div>
                <Button
                  size="sm"
                  variant="outline-neutral"
                  onClick={() => {
                    toast.dismiss(id);
                  }}
                >
                  {t('common.try_again')}
                </Button>
              </div>
            ));
          },
        });
      }
    },
    [
      form,
      t,
      isWorkvivoEditing,
      datasource,
      datasources,
      selectedSpaceIds,
      updateDatasource,
      createDatasource,
      dispatch,
      handleCancel,
    ]
  );

  // Load datasource data only once when editing an existing Workvivo datasource
  useEffect(() => {
    async function initializeEditMode() {
      if (!initializedRef.current && isWorkvivoEditing && isWorkvivoConfig(datasource.config)) {
        initializedRef.current = true;

        // Set form values from existing datasource
        form.setValue('client_id', datasource.config.client_id, { shouldDirty: false });
        form.setValue('access_token', datasource.config.access_token, { shouldDirty: false });
        form.setValue('space_ids', datasource.config.space_ids || [], { shouldDirty: false });
        form.setValue('pages', datasource.config.pages ?? true, { shouldDirty: false });
        form.setValue('articles', datasource.config.articles ?? true, { shouldDirty: false });
        form.setValue('goals', datasource.config.goals ?? true, { shouldDirty: false });

        // Set local state for spaces
        setSelectedSpaceIds(datasource.config.space_ids || []);

        // Show spaces section if there are selected spaces
        if (datasource.config.space_ids && datasource.config.space_ids.length > 0) {
          setShowSpacesSection(true);
        }

        // Set keep_published value
        if (isWorkvivoEditing) {
          form.setValue('keep_published', true);
        }

        // Fetch spaces to populate the UI
        await fetchWorkvivoSpaces(datasource.config.client_id, datasource.config.access_token);
      }
    }

    initializeEditMode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorkvivoEditing, form, datasource]); // fetchWorkvivoSpaces intentionally excluded to prevent infinite loops

  return (
    <>
      <SidebarWrapperContent className={EXTERNAL_SOURCE_SECTION}>
        <FormField
          control={form.control}
          name="client_id"
          render={({ field }) => (
            <FormItem className={EXTERNAL_SOURCE_FIELD}>
              <FormLabel>{t('integrations.workvivo.auth.client_id')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value.trim());
                  }}
                  placeholder={t('integrations.workvivo.auth.client_id_placeholder')}
                  readOnly={!canWrite}
                  type="text"
                />
              </FormControl>
              <FormDescription>
                {t('integrations.workvivo.auth.client_id_description')}
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="access_token"
          render={({ field }) => (
            <FormItem className={EXTERNAL_SOURCE_FIELD}>
              <FormLabel>{t('integrations.workvivo.auth.token')}</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  onChange={(e) => {
                    field.onChange(e.target.value.trim());
                  }}
                  placeholder={t('integrations.workvivo.auth.token_placeholder')}
                  readOnly={!canWrite}
                  type="text"
                />
              </FormControl>
              <FormDescription>{t('integrations.workvivo.auth.token_description')}</FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {showContentTypes && (
          <>
            <Separator />
            <div className="space-y-4">
              <h3 className="text-sm font-medium">{t('integrations.workvivo.sections')}</h3>

              <FormField
                control={form.control}
                name="pages"
                render={({ field }) => (
                  <FormItem className="flex gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!canWrite}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-normal">
                      {t('integrations.workvivo.content_types.global_pages')}
                    </FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="articles"
                render={({ field }) => (
                  <FormItem className="flex gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!canWrite}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-normal">
                      {t('integrations.workvivo.content_types.articles')}
                    </FormLabel>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="goals"
                render={({ field }) => (
                  <FormItem className="flex gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                        disabled={!canWrite}
                      />
                    </FormControl>
                    <FormLabel className="text-sm font-normal">
                      {t('integrations.workvivo.content_types.goals')}
                    </FormLabel>
                  </FormItem>
                )}
              />
            </div>
          </>
        )}

        {/* Spaces Selection */}
        {isSpacesLoading && <SpaceSkeleton />}

        {showSpaces && (
          <>
            <Separator />
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium">{t('integrations.workvivo.spaces.title')}</h3>
                <Switch
                  checked={showSpacesSection}
                  onCheckedChange={setShowSpacesSection}
                  disabled={!canWrite}
                />
              </div>

              {showSpacesSection && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {t('integrations.workvivo.spaces.selected_count', {
                        0: selectedSpaceIds.length,
                        1: workvivoSpaces.length,
                      })}
                    </span>
                    <div className="flex items-center gap-2 text-sm text-primary">
                      <button
                        type="button"
                        onClick={() => handleSelectAllSpaces(false)}
                        disabled={!canWrite}
                        className="text-xs text-primary hover:underline disabled:opacity-50"
                      >
                        {t('common.clear')}
                      </button>
                      <span className="text-muted-foreground">•</span>
                      <button
                        type="button"
                        onClick={() => handleSelectAllSpaces(true)}
                        disabled={!canWrite}
                        className="text-xs text-primary hover:underline disabled:opacity-50"
                      >
                        {t('common.all')}
                      </button>
                    </div>
                  </div>

                  <div className="max-h-48 space-y-2 overflow-y-auto">
                    {workvivoSpaces.map((space) => (
                      <div key={space.id} className="flex items-start gap-3">
                        <Checkbox
                          checked={selectedSpaceIds.includes(space.id.toString())}
                          onCheckedChange={(checked) =>
                            handleSpaceSelection(space.id.toString(), checked as boolean)
                          }
                          disabled={!canWrite}
                          className="mt-0.5"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="truncate text-sm font-medium">{space.name}</div>
                          {space.description && (
                            <div className="truncate text-xs text-muted-foreground">
                              {space.description}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </>
        )}

        {showKeepPublished && (
          <FormField
            control={form.control}
            name="keep_published"
            render={({ field }) => (
              <FormItem className="flex gap-2 space-y-0 rounded-md border p-4">
                <FormControl>
                  <Checkbox
                    checked={field.value}
                    onCheckedChange={field.onChange}
                    disabled={!canWrite}
                  />
                </FormControl>

                <FormLabel className="flex flex-col items-start gap-1 leading-none">
                  {t('collections.add_faq.keep_published')}

                  <FormDescription>
                    {t('collections.add_faq.keep_published_description')}
                  </FormDescription>
                </FormLabel>
              </FormItem>
            )}
          />
        )}
      </SidebarWrapperContent>

      {showButtons && (
        <SidebarWrapperFooter className={EXTERNAL_SOURCE_FOOTER}>
          <Button type="button" variant="ghost-neutral" onClick={() => handleCancel(true)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSubmit} type="submit" disabled={isSubmitDisabled}>
            {isValidating
              ? t('common.validating')
              : isWorkvivoEditing
                ? t('common.update')
                : t('common.add')}
          </Button>
        </SidebarWrapperFooter>
      )}
    </>
  );
};
